// ACTA_MEETING_SHARE_LINKS_SHARED_V1

function meetingDateTime(meeting = {}) {
  if (
    !meeting?.date ||
    !meeting?.start
  ) {
    return null;
  }

  const start =
    new Date(
      `${meeting.date}T${meeting.start}`
    );

  const durationMinutes =
    Number(
      meeting.durationMinutes || 30
    );

  const end =
    meeting.end
      ? new Date(
          `${meeting.date}T${meeting.end}`
        )
      : new Date(
          start.getTime() +
          durationMinutes *
            60 *
            1000
        );

  if (
    Number.isNaN(
      start.getTime()
    ) ||
    Number.isNaN(
      end.getTime()
    )
  ) {
    return null;
  }

  return {
    start,
    end,
  };
}


function compactCalendarDate(date) {
  return date
    .toISOString()
    .replace(
      /[-:]/g,
      ""
    )
    .replace(
      /\.\d{3}/,
      ""
    );
}


function normalizeWhatsappPhone(
  value = ""
) {
  const raw =
    String(
      value || ""
    ).trim();

  if (!raw) {
    return "";
  }

  const digits =
    raw.replace(
      /[^\d+]/g,
      ""
    );

  if (
    digits.startsWith("+")
  ) {
    return digits.slice(1);
  }

  if (
    digits.startsWith("00")
  ) {
    return digits.slice(2);
  }

  if (
    digits.startsWith("0")
  ) {
    return (
      `33${digits.slice(1)}`
    );
  }

  return digits;
}


function whatsappUrl(
  phone = "",
  text = ""
) {
  const normalized =
    normalizeWhatsappPhone(
      phone
    );

  const encoded =
    encodeURIComponent(text);

  return normalized
    ? (
        `https://wa.me/${normalized}` +
        `?text=${encoded}`
      )
    : (
        `https://wa.me/` +
        `?text=${encoded}`
      );
}


export function buildProspectMeetingAddress(
  prospect
) {
  const source =
    prospect || {};

  const nestedAddress =
    source.address &&
    typeof source.address ===
      "object"
      ? source.address
      : {};

  const street =
    typeof source.address ===
      "string"
      ? source.address
      : (
          source.street ||
          source.addressLine1 ||
          nestedAddress.street ||
          nestedAddress.line1 ||
          ""
        );

  const postalCode =
    source.postalCode ||
    source.zipCode ||
    nestedAddress.postalCode ||
    nestedAddress.zipCode ||
    "";

  const city =
    source.city ||
    nestedAddress.city ||
    "";

  const country =
    source.country ||
    nestedAddress.country ||
    "";

  return [
    street,
    [
      postalCode,
      city,
    ]
      .filter(Boolean)
      .join(" "),
    country,
  ]
    .filter(Boolean)
    .join(", ");
}


function meetingContactName(
  prospect = {}
) {
  return (
    prospect?.company ||
    [
      prospect?.firstName,
      prospect?.lastName,
    ]
      .filter(Boolean)
      .join(" ") ||
    "Contact"
  );
}


export function buildMeetingInvitationShareLinks(
  meeting = {},
  prospect = {}
) {
  const contactName =
    meetingContactName(
      prospect
    );

  const subject =
    String(
      meeting.subject ||
      `Rendez-vous ${contactName}`
    ).trim();

  const description =
    String(
      meeting.notes ||
      meeting.description ||
      ""
    ).trim();

  const location =
    meeting.meetingType ===
      "physical"
      ? String(
          meeting.location || ""
        ).trim()
      : "";

  const title =
    `Invitation - ${subject}`;

  const body = [
    "Bonjour,",
    "",
    "Voici les informations de notre rendez-vous :",
    `Objet : ${subject}`,
    `Date : ${meeting.date || ""}`,
    `Heure : ${meeting.start || ""}`,
    `Durée : ${meeting.durationMinutes || 30} minutes`,
    `Lieu : ${location || "À préciser"}`,
    description
      ? `Description : ${description}`
      : "",
    "",
    "Cordialement",
  ]
    .filter(
      (line) =>
        line !== ""
        || true
    )
    .join("\n");

  const recipient =
    String(
      prospect?.email || ""
    ).trim();

  const gmailParams =
    new URLSearchParams({
      view: "cm",
      fs: "1",
      to: recipient,
      su: title,
      body,
    });

  return {
    gmail:
      `https://mail.google.com/mail/?${gmailParams.toString()}`,

    mailto:
      `mailto:${encodeURIComponent(recipient)}` +
      `?subject=${encodeURIComponent(title)}` +
      `&body=${encodeURIComponent(body)}`,

    whatsapp:
      whatsappUrl(
        prospect?.phone ||
        prospect?.mobile ||
        "",
        `${title}\n\n${body}`
      ),
  };
}


export function buildMeetingCalendarLinks(
  meeting = {},
  prospect = {}
) {
  const range =
    meetingDateTime(
      meeting
    );

  if (!range) {
    return null;
  }

  const contactName =
    meetingContactName(
      prospect
    );

  const title =
    String(
      meeting.subject ||
      `Rendez-vous ${contactName}`
    ).trim();

  const details =
    String(
      meeting.notes ||
      meeting.description ||
      ""
    ).trim();

  const location =
    meeting.meetingType ===
      "physical"
      ? String(
          meeting.location || ""
        ).trim()
      : "";

  const attendee =
    String(
      prospect?.email || ""
    ).trim();

  const dates =
    `${compactCalendarDate(
      range.start
    )}/${compactCalendarDate(
      range.end
    )}`;

  const googleParams =
    new URLSearchParams({
      action: "TEMPLATE",
      text: title,
      dates,
      details,
      location,
    });

  if (attendee) {
    googleParams.append(
      "add",
      attendee
    );
  }

  const outlookParams =
    new URLSearchParams({
      path:
        "/calendar/action/compose",
      rru: "addevent",
      subject: title,
      startdt:
        range.start.toISOString(),
      enddt:
        range.end.toISOString(),
      body: details,
      location,
    });

  if (attendee) {
    outlookParams.append(
      "requiredattendees",
      attendee
    );
  }

  return {
    google:
      `https://calendar.google.com/calendar/render?${googleParams.toString()}`,

    outlook:
      `https://outlook.live.com/calendar/0/deeplink/compose?${outlookParams.toString()}`,
  };
}


export const DEFAULT_MEETING_DESCRIPTION =
  "J'ai le plaisir de vous confirmer notre rendez-vous.";


function personName(person = {}) {
  return [
    person?.firstName,
    person?.lastName,
  ]
    .filter(Boolean)
    .join(" ")
    .trim();
}


export function buildMeetingDefaultSubject(
  prospect = {},
  commercial = {}
) {
  const clientName =
    personName(prospect) ||
    prospect?.company ||
    "Client";

  const commercialName =
    personName(commercial) ||
    "Commercial";

  return `${clientName} / ALMA - ${commercialName}`;
}


// Alias conservé pour ne pas casser ProspectDetail.
export const buildStandaloneMeetingLinks =
  buildMeetingCalendarLinks;
